import { t } from "i18next";
import { useMemo } from "react";
import { FormProvider, useForm } from "react-hook-form";

import { showErrorToast } from "#app/notifications";
import { CreateMaterialLayoutDto } from "#modules/materialLayouts/infra/createMaterialLayout.api";
import { useCreateMaterialLayout } from "#modules/materialLayouts/view/useCreateMaterialLayout";
import { useUpdateMaterialLayout } from "#modules/materialLayouts/view/useUpdateMaterialLayout";
import { useCreateMaterialsVolumetricPrice } from "#modules/materials/view/useCreateMaterialsVolumetricPrice";
import { useUpdateMaterialsVolumetricPrice } from "#modules/materials/view/useUpdateMaterialsVolumetricPrice";
import { QuoteRequestLineDto } from "#modules/quoteRequestLines/infra/fetchQuoteRequestLine.api";
import { PartHeader } from "#modules/quoteRequestLines/views/QuoteRequestLine/PartHeader";
import { useAnalytics } from "#shared/useAnalytics";
import { CheckboxController } from "#shared/view/components/CheckboxController";
import { Flex } from "#shared/view/components/Flex";
import { Stack } from "#shared/view/components/Stack";
import { Button } from "#shared/view/designSystem/Button";
import { RecommendedPriceInputController } from "#shared/view/designSystem/inputs/RecommendedPriceInputController";
import {
  getMaterialDensity,
  MaterialGradeType,
  PartTypeType,
  PricingModeSchema,
  round,
} from "shared";

export type VolumetricMaterialFormData = {
  volumetricPrice: number;
  createMaterialToCatalog: boolean;
  updateMaterialInCatalog: boolean;
  applyToAllSiblings: boolean;
};

type Props = {
  quoteRequestLine: QuoteRequestLineDto;
  close: () => void;
  cancel: () => void;
  materialGrade: MaterialGradeType;
  partType: PartTypeType | null;
  hasChildren: boolean;
};

export const VolumetricMaterialLayoutForm = ({
  quoteRequestLine,
  close,
  cancel,
  materialGrade,
  partType,
  hasChildren,
}: Props) => {
  const analytics = useAnalytics();

  const existingMaterialLayout = useMemo(
    () => quoteRequestLine.layout?.materialLayout,
    [quoteRequestLine],
  );

  const { mutateAsync: createVolumetricInCatalog } = useCreateMaterialsVolumetricPrice();
  const { mutateAsync: updateVolumetricInCatalog } = useUpdateMaterialsVolumetricPrice();

  const { updateMaterialLayoutAsync, isPending: isUpdatePending } = useUpdateMaterialLayout({
    quoteRequestLineId: quoteRequestLine.id,
  });

  const { createMaterialLayoutAsync, isPending: isCreatePending } = useCreateMaterialLayout({
    layoutId: quoteRequestLine.layoutId,
    quoteRequestLineId: quoteRequestLine.id,
  });

  const isPending = isUpdatePending || isCreatePending;

  const hasExistingCatalogMaterial = !!existingMaterialLayout?.materialVolumetricId;

  const formDefaultValues = {
    volumetricPrice: existingMaterialLayout?.volumetricPrice ?? 0,
    createMaterialToCatalog: false,
    updateMaterialInCatalog: false,
    applyToAllSiblings: false,
  };

  const formMethods = useForm<VolumetricMaterialFormData>({
    defaultValues: formDefaultValues,
  });

  const { handleSubmit, control } = formMethods;

  const handleValidate = async (data: VolumetricMaterialFormData) => {
    let createdMaterialVolumetricId: string | undefined;

    if (data.createMaterialToCatalog && !hasExistingCatalogMaterial) {
      const createdMaterial = await createVolumetricInCatalog({
        materialGrade,
        volumetricPrice: round(data.volumetricPrice),
        name: quoteRequestLine.materialName,
      });
      createdMaterialVolumetricId = createdMaterial.id;
    }

    if (data.updateMaterialInCatalog && hasExistingCatalogMaterial) {
      void updateVolumetricInCatalog({
        id: existingMaterialLayout.materialVolumetricId!,
        materialGrade,
        volumetricPrice: round(data.volumetricPrice),
        name: quoteRequestLine.materialName,
      });
    }

    const materialVolumetricId = getNewMaterialVolumetricId(
      data,
      existingMaterialLayout,
      createdMaterialVolumetricId,
    );

    if (!existingMaterialLayout) {
      if (!quoteRequestLine.layoutId) {
        showErrorToast({ title: "Impossible de créer le matériau" });

        return;
      }

      const part = quoteRequestLine.part;
      const createPayload: CreateMaterialLayoutDto = {
        quoteRequestId: quoteRequestLine.quoteRequestId,
        volumetricPrice: round(data.volumetricPrice),
        density: getMaterialDensity({ materialGrade }),
        length: part.length ?? 0,
        width: part.width ?? 0,
        height: part.height ?? 0,
        tubeThickness: part.tubeThickness ?? undefined,
        layoutId: quoteRequestLine.layoutId,
        quantity: 1,
        pricingMode: PricingModeSchema.enum.VOLUMETRIC,
        type: partType ?? undefined,
        materialVolumetricId: materialVolumetricId ?? undefined,
      };

      await createMaterialLayoutAsync({
        payload: createPayload,
      });
      analytics.capture("quote_request_line:material_layout_create", {
        quote_request_line_id: quoteRequestLine.id,
      });
    } else {
      await updateMaterialLayoutAsync({
        id: existingMaterialLayout.id,
        payload: {
          data: {
            ...existingMaterialLayout,
            volumetricPrice: round(data.volumetricPrice),
            materialVolumetricId,
          },
          applyToAllSiblings: data.applyToAllSiblings,
        },
      });
      analytics.capture("quote_request_line:material_layout_update", {
        quote_request_line_id: quoteRequestLine.id,
      });
    }

    formMethods.reset(formDefaultValues);

    close();
  };

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleValidate)}>
        <Stack gap="sm">
          <PartHeader
            quoteRequestLine={quoteRequestLine}
            disablePartTypeChip
            hasChildren={hasChildren}
            isConsumable={false}
            thumbnailSize={{ width: 100, height: 100 }}
            fontSize={24}
            part={quoteRequestLine.part}
            showWeight
          />
          <RecommendedPriceInputController
            name="volumetricPrice"
            label={t("settings.materials.volumetricPrice")}
            unit="€/kg"
            control={control}
            required
          />
          {!hasExistingCatalogMaterial && (
            <CheckboxController
              name="createMaterialToCatalog"
              control={control}
              label={t("material.createMaterialToCatalog")}
            />
          )}
          {hasExistingCatalogMaterial && (
            <>
              <CheckboxController
                name="updateMaterialInCatalog"
                control={control}
                label={t("material.updateMaterialInCatalog")}
              />
              <CheckboxController
                name="applyToAllSiblings"
                control={control}
                label={t("material.applyToAllSiblings")}
              />
            </>
          )}
          <Flex gap="xs" justify="end">
            <Button variant="outline" onClick={cancel}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" disabled={isPending} loading={isPending}>
              {t("common.validate")}
            </Button>
          </Flex>
        </Stack>
      </form>
    </FormProvider>
  );
};

const getNewMaterialVolumetricId = (
  data: VolumetricMaterialFormData,
  existingMaterialLayout:
    | NonNullable<NonNullable<QuoteRequestLineDto["layout"]>["materialLayout"]>
    | null
    | undefined,
  createdMaterialVolumetricId: string | undefined,
): string | null => {
  if (data.createMaterialToCatalog) {
    return createdMaterialVolumetricId ?? null;
  }
  if (data.updateMaterialInCatalog) {
    return existingMaterialLayout?.materialVolumetricId ?? null;
  }

  return existingMaterialLayout?.materialVolumetricId ?? null;
};
